import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'btn-gradient focus-visible:ring-brand-500',
        destructive: 'bg-error text-white hover:opacity-90 focus-visible:ring-error',
        outline: 'border bg-surface text-primary hover:bg-surface-secondary focus-visible:ring-brand-500',
        secondary: 'bg-surface-secondary text-primary hover:bg-[var(--border-default)] focus-visible:ring-gray-500',
        success: 'bg-success text-white hover:opacity-90 focus-visible:ring-success',
        warning: 'bg-warning text-[#081631] hover:opacity-90 focus-visible:ring-warning',
        ghost: 'hover:bg-surface-secondary text-primary',
        link: 'text-brand-500 underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-11 px-4 py-2 sm:h-10',
        sm: 'h-10 rounded-md px-3 text-xs sm:h-8',
        lg: 'h-12 rounded-lg px-6 text-base',
        icon: 'h-11 w-11 sm:h-10 sm:w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, children, disabled, ...props }, ref) => {
    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {children}
      </button>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
